/**
 * Centralized configuration for marketing pages
 * This file contains all the data used across marketing pages to ensure consistency
 */

import {
  getProviderAwareModelOliveCost,
  isModelSunset,
  MODEL_OLIVE_COSTS,
  OLIVE_VALUE_USD,
  type PricedModelId,
  type ModelOliveCost,
} from '@/types/shared'

// Re-export for consumers that import from here
export { OLIVE_VALUE_USD }

// ============================================
// Constants
// ============================================

/** Convert USD to Olives */
export const usdToOlives = (usd: number): number => usd / OLIVE_VALUE_USD

/**
 * Canonical "what do N olives buy" framing — every olive quantity shown to
 * users is denominated in outcomes through this one helper so the figures
 * never diverge across surfaces. Reference models:
 * - video: Kling 3.0 Pro ('kling-v3'), base per-second rate without audio —
 *   the mainstream flagship rate
 * - images: Nano Banana 2 ('nano-banana-2'), 1K per-asset rate — the cheap
 *   mainstream still model
 * Rounded down so copy under-promises; images floored to the nearest 10.
 * The pricing page's per-model OliveExplainer table intentionally does NOT
 * use this — it shows exact per-model rates, which is its whole job.
 */
export function oliveOutcomes(olives: number): { videoSeconds: number; images: number } {
  const videoRate = (MODEL_OLIVE_COSTS['kling-v3'] as ModelOliveCost).olivesPerSecond ?? 1
  const imageRate = (MODEL_OLIVE_COSTS['nano-banana-2'] as ModelOliveCost).olivesPerAsset ?? 0.7
  if (!Number.isFinite(olives) || olives <= 0) return { videoSeconds: 0, images: 0 }
  return {
    videoSeconds: Math.floor(olives / videoRate),
    images: Math.floor(olives / imageRate / 10) * 10,
  }
}

/**
 * The provider a public visitor's generations actually route to.
 *
 * Seedance is the only family whose rate card is provider-dependent, and
 * production runs `SEEDANCE_20_PROVIDER=fal` — so a visitor pays the Fal card
 * (~5% under Fal's list price). `MODEL_OLIVE_COSTS` holds the cheaper native
 * BytePlus rates, which only the allowlisted orgs in `seedance20ProviderConfig`
 * receive. Marketing pages must quote what a
 * visitor pays, so they resolve through the same provider-aware lookup billing
 * uses. Every non-Seedance model falls through to `MODEL_OLIVE_COSTS` unchanged.
 *
 * If the Seedance rollout switch ever flips back to BytePlus for everyone, this
 * constant is the one line to change.
 */
const PUBLIC_GENERATION_PROVIDER = 'fal.ai'

/** The rate card a public visitor is billed on — not the allowlisted-org card. */
const publicOliveCost = (modelId: PricedModelId): ModelOliveCost =>
  getProviderAwareModelOliveCost(modelId, PUBLIC_GENERATION_PROVIDER)

/** Get the USD price for a model from the centralized olive costs. */
const modelPriceUsd = (modelId: PricedModelId): number => {
  const cost = publicOliveCost(modelId)
  const olives =
    cost.olivesPerSecond ??
    cost.olivesPerAsset ??
    cost.olivesPerMinuteCeil ??
    cost.olivesPerThousandChars ??
    cost.minimumOlives ??
    0
  return olives * OLIVE_VALUE_USD
}

const resolutionLabel = (maxHeight: number): string => {
  if (maxHeight === 1440) return '2K'
  if (maxHeight === 2160) return '4K'
  return `${maxHeight}p`
}

const fallbackResolutionLabel = (maxHeight: number): string => {
  if (maxHeight < 720) return '720p'
  if (maxHeight < 1080) return '1080p'
  if (maxHeight < 2160) return '4K'
  return 'Base'
}

export interface PricingVariant {
  label: string
  priceUsd: number
  resolutionPrefix?: string
  audioMode?: 'none' | 'audio' | 'voice'
}

const pricingVariants = (modelId: PricedModelId): PricingVariant[] => {
  const cost = publicOliveCost(modelId)

  // Rate cards keyed by a render-quality selector (FLUX.3: draft vs full, each
  // with its own resolution matrix). Must run before the plain per-second branch
  // below — these models also carry a top-level olivesPerSecond, which is the
  // FULL rate, so falling through would price every one of them at full and
  // hide the draft rate that's the whole point of having the selector.
  // ponytail: no audio-surcharge expansion here — no quality-tiered model levies
  // one (FLUX.3's audio is included). Reuse the surcharge fan-out below if that changes.
  if (cost.perSecondQualityTiers) {
    return Object.entries(cost.perSecondQualityTiers).flatMap(([quality, rate]) => {
      const qualityLabel = quality.charAt(0).toUpperCase() + quality.slice(1)
      const tiers = rate.resolutionTiers ?? []
      const variants: PricingVariant[] = tiers.map(tier => ({
        label: `${qualityLabel} ${resolutionLabel(tier.maxHeight)}`,
        priceUsd: tier.olivesPerSecond * OLIVE_VALUE_USD,
        resolutionPrefix: resolutionLabel(tier.maxHeight),
        audioMode: 'none' as const,
      }))
      // resolutionTiers only cover the heights they cap; the quality's own
      // olivesPerSecond is the rate above the tallest tier.
      if (!variants.some(variant => variant.priceUsd === rate.olivesPerSecond * OLIVE_VALUE_USD)) {
        const lastTier = tiers[tiers.length - 1]
        const prefix = lastTier ? fallbackResolutionLabel(lastTier.maxHeight) : ''
        variants.push({
          label: prefix ? `${qualityLabel} ${prefix}` : qualityLabel,
          priceUsd: rate.olivesPerSecond * OLIVE_VALUE_USD,
          resolutionPrefix: prefix || undefined,
          audioMode: 'none' as const,
        })
      }
      return variants
    })
  }

  if (cost.olivesPerSecond !== undefined) {
    const tiers = cost.resolutionTiers ?? [{ maxHeight: 0, olivesPerSecond: cost.olivesPerSecond }]
    const variants = tiers.flatMap(tier => {
      const prefix = tier.maxHeight ? resolutionLabel(tier.maxHeight) : ''
      const audio = tier.audioSurchargeOlives ?? cost.audioSurchargeOlives ?? 0
      const voice = cost.voiceSurchargeOlives ?? 0
      const noAudio = {
        label: audio > 0 ? `${prefix ? `${prefix} ` : ''}no audio` : prefix || 'Base',
        priceUsd: tier.olivesPerSecond * OLIVE_VALUE_USD,
        resolutionPrefix: prefix || undefined,
        audioMode: 'none' as const,
      }
      if (audio <= 0) return [noAudio]
      return [
        noAudio,
        {
          label: `${prefix ? `${prefix} ` : ''}with audio`,
          priceUsd: (tier.olivesPerSecond + audio) * OLIVE_VALUE_USD,
          resolutionPrefix: prefix || undefined,
          audioMode: 'audio' as const,
        },
        ...(voice > 0
          ? [
              {
                label: `${prefix ? `${prefix} ` : ''}audio + voice control`,
                priceUsd: (tier.olivesPerSecond + audio + voice) * OLIVE_VALUE_USD,
                resolutionPrefix: prefix || undefined,
                audioMode: 'voice' as const,
              },
            ]
          : []),
      ]
    })
    if (!variants.some(variant => variant.priceUsd === cost.olivesPerSecond! * OLIVE_VALUE_USD)) {
      const lastTier = tiers[tiers.length - 1]
      const prefix = lastTier?.maxHeight ? fallbackResolutionLabel(lastTier.maxHeight) : ''
      const audio = cost.audioSurchargeOlives ?? 0
      variants.push({
        label: audio > 0 ? `${prefix ? `${prefix} ` : ''}no audio` : prefix || 'Base',
        priceUsd: cost.olivesPerSecond * OLIVE_VALUE_USD,
        resolutionPrefix: prefix || undefined,
        audioMode: 'none' as const,
      })
      if (audio > 0) {
        variants.push({
          label: `${prefix ? `${prefix} ` : ''}with audio`,
          priceUsd: (cost.olivesPerSecond + audio) * OLIVE_VALUE_USD,
          resolutionPrefix: prefix || undefined,
          audioMode: 'audio' as const,
        })
        if (cost.voiceSurchargeOlives) {
          variants.push({
            label: `${prefix ? `${prefix} ` : ''}audio + voice control`,
            priceUsd: (cost.olivesPerSecond + audio + cost.voiceSurchargeOlives) * OLIVE_VALUE_USD,
            resolutionPrefix: prefix || undefined,
            audioMode: 'voice' as const,
          })
        }
      }
    }
    return variants
  }

  if (cost.imageResolutionTiers && cost.imageResolutionTiers.length > 0) {
    return cost.imageResolutionTiers.flatMap(tier => {
      if (tier.qualityOlivesPerAsset) {
        return Object.entries(tier.qualityOlivesPerAsset).map(([quality, olives]) => ({
          label: `${tier.resolution} ${quality}`,
          priceUsd: olives * OLIVE_VALUE_USD,
        }))
      }
      return [{ label: tier.resolution, priceUsd: tier.olivesPerAsset * OLIVE_VALUE_USD }]
    })
  }

  const olives = cost.olivesPerAsset ?? 0
  return [{ label: 'Base', priceUsd: olives * OLIVE_VALUE_USD }]
}

export const getModelPricingVariants = (modelId: string): PricingVariant[] | undefined => {
  if (!(modelId in MODEL_OLIVE_COSTS)) return undefined
  return pricingVariants(modelId as PricedModelId)
}

// ============================================
// Shot Budget (/production-calculator) rates
// ============================================

/** The handful of public rates the shot-budget page prices against. */
export interface ShotBudgetRates {
  oliveUsd: number
  /** USD per generated second. `draft` is the 480p tier the page assumes for exploratory takes. */
  perSecondUsd: { draft: number; '720': number; '1080': number; '4k': number }
  /** USD per look-dev still. */
  stillUsd: number
  /** USD per finished second for the SDR→HDR pass (Topaz Hyperion on Martini), by delivery resolution. */
  hdrPerSecondUsd: { '720': number; '1080': number; '4k': number }
}

const tierUsd = (modelId: PricedModelId, maxHeight: number): number => {
  const tier = publicOliveCost(modelId).resolutionTiers?.find(t => t.maxHeight === maxHeight)
  if (!tier) throw new Error(`Shot budget: ${modelId} has no ${maxHeight}p tier on the public rate card`)
  return tier.olivesPerSecond * OLIVE_VALUE_USD
}

/**
 * Resolves the /production-calculator page's rates from the same card /pricing prints, by
 * model id and tier — never a copied number. Throws (and so fails the unit
 * test) if a mapped tier is retired, instead of silently going stale.
 */
export function getShotBudgetRates(): ShotBudgetRates {
  const still = publicOliveCost('seedream-v5-pro').imageResolutionTiers?.find(t => t.resolution === '1K')
  if (!still) throw new Error('Shot budget: seedream-v5-pro has no 1K tier on the public rate card')
  const hdr4k = publicOliveCost('topaz-video-enhance-hyperion').olivesPerSecond
  if (hdr4k === undefined)
    throw new Error('Shot budget: topaz-video-enhance-hyperion has no per-second rate on the public rate card')
  return {
    oliveUsd: OLIVE_VALUE_USD,
    perSecondUsd: {
      draft: tierUsd('seedance-2.5', 480),
      '720': tierUsd('seedance-2.5', 720),
      '1080': tierUsd('seedance-2.5', 1080),
      // Seedance 2.5 has no 4K tier; 2.0 is the public 4K route.
      '4k': tierUsd('seedance-2.0', 2160),
    },
    stillUsd: still.olivesPerAsset * OLIVE_VALUE_USD,
    hdrPerSecondUsd: {
      '720': tierUsd('topaz-video-enhance-hyperion', 1440),
      '1080': tierUsd('topaz-video-enhance-hyperion', 1440),
      '4k': hdr4k * OLIVE_VALUE_USD,
    },
  }
}

/** Priority access form URL */
export const PRIORITY_ACCESS_FORM_URL = 'https://forms.gle/JEHKPCBe4vubSJ988'

// ============================================
// AI Model Data
// ============================================

export interface AIModel {
  key: string
  name: string
  slug: string
  description: string
  features: string[]
  website: string
  /** Price per second in USD (for video) or per generation (for stills) */
  priceUsd: number
  pricingVariants?: PricingVariant[]
}

export interface ModelCategory {
  category: string
  models: AIModel[]
}

/**
 * Drop models past their provider sunset (`MODEL_SUNSETS` in shared types).
 *
 * The catalogs below are the full roster as authored; a page applies this at
 * render time with its own clock and hands the result down, so the server
 * markup and the client hydrate from one decision. The marketing pages are
 * ISR (each declares an hourly `revalidate` floor; the [locale] layer's 60 s
 * i18n fetch makes it a minute in practice), which is how a retired model
 * leaves them on the day without a deploy. Do not apply this at module scope:
 * a server process that loaded the module before the sunset would keep listing
 * the model while a fresh client bundle dropped it.
 */
export const omitSunsetModels = (models: AIModel[], now: number = Date.now()): AIModel[] =>
  models.filter(model => !isModelSunset(model.key, now))

/** `omitSunsetModels` per category; a category left with no models goes too. */
export const omitSunsetCategories = (categories: ModelCategory[], now: number = Date.now()): ModelCategory[] =>
  categories
    .map(category => ({ ...category, models: omitSunsetModels(category.models, now) }))
    .filter(category => category.models.length > 0)

export const VIDEO_GENERATION_MODELS: ModelCategory[] = [
  {
    category: 'FLUX',
    models: [
      {
        key: 'flux-3',
        name: 'FLUX.3',
        slug: 'flux-3',
        description:
          "Black Forest Labs' video model. Up to twenty seconds in a single generation with native audio, ten keyframes pinned to exact timecodes, and a draft mode you can render up.",
        features: ['Start frame', 'End frame', 'Keyframes', 'Audio', 'Extend'],
        website: 'https://bfl.ai/',
        priceUsd: modelPriceUsd('flux-3'),
      },
      {
        key: 'flux-3-max',
        name: 'FLUX.3 Max',
        slug: 'flux-3-max',
        description:
          'FLUX.3 post-trained by fal Research and Black Forest Labs for prompt adherence and visual quality at much lower latency, tied #1 for image-to-video on Artificial Analysis. Up to twenty seconds at 720p or 1080p with native audio, first and last frames, and ten timed keyframes.',
        features: ['Start frame', 'End frame', 'Keyframes', 'Audio'],
        website: 'https://bfl.ai/',
        priceUsd: modelPriceUsd('flux-3-max'),
      },
    ],
  },
  {
    category: 'Veo',
    models: [
      {
        key: 'veo3.1',
        name: 'Veo 3.1',
        slug: 'veo-3-1',
        description:
          'High-quality video generation with native audio from Google DeepMind. Best for final outputs with start and end frame control.',
        features: ['Start frame', 'End frame', 'Audio'],
        website: 'https://deepmind.google/technologies/veo/',
        priceUsd: modelPriceUsd('veo3.1'),
      },
      {
        key: 'veo3.1-fast',
        name: 'Veo 3.1 Fast',
        slug: 'veo-3-1-fast',
        description: 'Faster variant of Veo 3.1 with native audio. Lower cost with quicker generation times.',
        features: ['Start frame', 'End frame', 'Audio'],
        website: 'https://deepmind.google/technologies/veo/',
        priceUsd: modelPriceUsd('veo3.1-fast'),
      },
    ],
  },
  {
    category: 'Kling',
    models: [
      {
        key: 'kling-v4',
        name: 'Kling 4',
        slug: 'kling-4',
        description:
          'Cinematic video up to 30s with steadier complex motion, consistent characters across references, and multi-shot.',
        features: ['Start frame', 'End frame', 'Elements', 'Multi-shot'],
        website: 'https://klingai.com/',
        priceUsd: modelPriceUsd('kling-v4'),
      },
      {
        key: 'kling-v3',
        name: 'Kling 3.0 Pro',
        slug: 'kling-3-0-pro',
        description:
          'Top-tier video generation with cinematic visuals, fluid motion, native audio, and element support.',
        features: ['Start frame', 'End frame', 'Audio', 'Elements', 'Multi-shot'],
        website: 'https://klingai.com/',
        priceUsd: modelPriceUsd('kling-v3'),
      },
      {
        key: 'kling-o3',
        name: 'Kling O3 Pro',
        slug: 'kling-o3-pro',
        description: 'Video generation with multi-shot support, extended durations up to 15s, and reference-to-video.',
        features: ['Start frame', 'End frame', 'Audio', 'Multi-shot'],
        website: 'https://klingai.com/',
        priceUsd: modelPriceUsd('kling-o3'),
      },
      {
        key: 'kling-2.6',
        name: 'Kling 2.6',
        slug: 'kling-2-6',
        description: 'Top-tier video generation with cinematic visuals, fluid motion, and native audio support.',
        features: ['Start frame', 'Audio'],
        website: 'https://klingai.com/',
        priceUsd: modelPriceUsd('kling-2.6'),
      },
      {
        key: 'kling-o1',
        name: 'Kling O1',
        slug: 'kling-o1',
        description: 'High-quality image-to-video generation with smooth motion and end frame control.',
        features: ['Start frame', 'End frame'],
        website: 'https://klingai.com/',
        priceUsd: modelPriceUsd('kling-o1'),
      },
    ],
  },
  {
    category: 'Sora',
    models: [
      {
        key: 'sora-2',
        name: 'Sora 2 Pro',
        slug: 'sora-2',
        description:
          "OpenAI's state-of-the-art video model with native audio, capable of creating richly detailed, dynamic clips from text or images.",
        features: ['Start frame', 'Audio'],
        website: 'https://openai.com/research/sora',
        priceUsd: modelPriceUsd('sora-2'),
      },
      {
        key: 'sora-2-standard',
        name: 'Sora 2',
        slug: 'sora-2-standard',
        description:
          "OpenAI's video model with native audio at 720p. An affordable option for dynamic clips from text or images.",
        features: ['Start frame', 'Audio'],
        website: 'https://openai.com/research/sora',
        priceUsd: modelPriceUsd('sora-2-standard'),
      },
    ],
  },
  {
    category: 'Hailuo',
    models: [
      {
        key: 'hailuo-03-max-turbo',
        name: 'MiniMax H3 Max Turbo',
        slug: 'minimax-h3-max-turbo',
        description:
          'A faster, half-price cut of MiniMax H3 Max at about 97% of its quality, with native audio and start/end frames at up to 1080p. Preview.',
        features: ['Start frame', 'End frame', 'Audio'],
        website: 'https://www.minimax.io/',
        priceUsd: modelPriceUsd('hailuo-03-max-turbo'),
      },
      {
        key: 'hailuo-03-max',
        name: 'MiniMax H3 Max',
        slug: 'minimax-h3-max',
        description:
          'MiniMax H3 post-trained for stronger prompt adherence and aesthetics, with native audio, start/end frames, and image, video, or audio references at up to 1080p. Extends a clip by up to 15 seconds at up to 2K, replaces a section of one with a new 5-13 second scene, and recasts the people in an existing clip from reference photos.',
        features: ['Start frame', 'End frame', 'Audio', 'Reference media', 'Extend', 'Insert scene', 'Recast'],
        website: 'https://www.minimax.io/',
        priceUsd: modelPriceUsd('hailuo-03-max'),
      },
      {
        key: 'hailuo-03',
        name: 'MiniMax H3',
        slug: 'minimax-h3',
        description: 'Multimodal 2K video generation with native stereo audio and image, video, or audio references.',
        features: ['Start frame', 'End frame', 'Audio', 'Reference media', '2K'],
        website: 'https://www.minimax.io/',
        priceUsd: modelPriceUsd('hailuo-03'),
      },
      {
        key: 'hailuo-02',
        name: 'Hailuo 02',
        slug: 'hailuo-02',
        description: "MiniMax's advanced video generation model with 1080p resolution and start/end frame control.",
        features: ['Start frame', 'End frame'],
        website: 'https://hailuoai.video/',
        priceUsd: modelPriceUsd('hailuo-02'),
      },
    ],
  },
  {
    category: 'Seedance',
    models: [
      {
        key: 'seedance-2.5',
        name: 'Seedance 2.5',
        slug: 'seedance-2-5',
        description:
          "ByteDance's latest Seedance model, with native 30-second single takes, an Auto duration option, end frame support, and a 50-file reference budget.",
        features: ['Start frame', 'End frame', 'Audio', 'Reference images', 'Elements', 'Auto duration', '30s'],
        website: 'https://seed.bytedance.com/en/seedance',
        priceUsd: modelPriceUsd('seedance-2.5'),
      },
      {
        key: 'seedance-2.0',
        name: 'Seedance 2.0',
        slug: 'seedance-2-0',
        description:
          "ByteDance's native ModelArk video generation with cinematic output, audio, reference media, and up to 4K resolution.",
        features: ['Start frame', 'End frame', 'Audio', 'Reference images', 'Elements', '4K'],
        website: 'https://seed.bytedance.com/en/seedance',
        priceUsd: modelPriceUsd('seedance-2.0'),
      },
      {
        key: 'seedance-2.0-fast',
        name: 'Seedance 2.0 Fast',
        slug: 'seedance-2-0-fast',
        description: 'Faster native Seedance 2.0 generation with audio and reference media, capped at 720p.',
        features: ['Start frame', 'End frame', 'Audio', 'Reference images', 'Elements'],
        website: 'https://seed.bytedance.com/en/seedance',
        priceUsd: modelPriceUsd('seedance-2.0-fast'),
      },
      {
        key: 'seedance-2.0-mini',
        name: 'Seedance 2.0 Mini',
        slug: 'seedance-2-0-mini',
        description:
          'The quickest, most affordable native Seedance 2.0 tier with audio and reference media, capped at 720p.',
        features: ['Start frame', 'End frame', 'Audio', 'Reference images', 'Elements'],
        website: 'https://seed.bytedance.com/en/seedance',
        priceUsd: modelPriceUsd('seedance-2.0-mini'),
      },
      {
        key: 'seedance-1.5',
        name: 'Seedance 1.5',
        slug: 'seedance-1-5',
        description:
          'High-quality video generation from ByteDance with native audio support and flexible durations from 4-12 seconds.',
        features: ['Start frame', 'End frame', 'Audio'],
        website: 'https://seed.bytedance.com/en/seedance',
        priceUsd: modelPriceUsd('seedance-1.5'),
      },
    ],
  },
  {
    category: 'Grok',
    models: [
      {
        key: 'grok-imagine',
        name: 'Grok Imagine',
        slug: 'grok-imagine',
        description:
          'Video generation from xAI with flexible durations from 1-15 seconds and a wide range of aspect ratios.',
        features: ['Start frame'],
        website: 'https://x.ai/',
        priceUsd: modelPriceUsd('grok-imagine'),
      },
      {
        key: 'grok-imagine-1.5',
        name: 'Grok Imagine 1.5',
        slug: 'grok-imagine-1-5',
        description:
          'Text, image, and reference video generation from xAI with native audio, durations up to 15 seconds, and up to 1080p output.',
        features: ['Start frame', 'References', 'Audio', '1080p'],
        website: 'https://x.ai/',
        priceUsd: modelPriceUsd('grok-imagine-1.5'),
      },
      {
        key: 'grok-imagine-1.5-lite',
        name: 'Grok Imagine 1.5 Lite',
        slug: 'grok-imagine-1-5-lite',
        description:
          'Budget text and image video generation from xAI with native audio, durations up to 15 seconds, and 480p to 1080p output.',
        features: ['Start frame', 'Audio', '1080p'],
        website: 'https://x.ai/',
        priceUsd: modelPriceUsd('grok-imagine-1.5-lite'),
      },
    ],
  },
  {
    category: 'Pixverse',
    models: [
      {
        key: 'pixverse-v6',
        name: 'Pixverse V6',
        slug: 'pixverse-v6',
        description:
          'High-quality video generation with native audio, flexible durations up to 15 seconds, and art style presets.',
        features: ['Start frame', 'Audio'],
        website: 'https://pixverse.ai/',
        priceUsd: modelPriceUsd('pixverse-v6'),
      },
    ],
  },
  {
    category: 'LTX',
    models: [
      {
        key: 'ltx-2.3',
        name: 'LTX 2.3',
        slug: 'ltx-2-3',
        description:
          "Lightricks' open-source 4K video model with native audio. Supports start/end frame control and durations up to 10 seconds.",
        features: ['Start frame', 'End frame', 'Audio'],
        website: 'https://www.lightricks.com/',
        priceUsd: modelPriceUsd('ltx-2.3'),
      },
    ],
  },
  {
    category: 'Creatify',
    models: [
      {
        key: 'boreal',
        name: 'Boreal by Creatify',
        slug: 'boreal',
        description:
          'Product, UGC, and presenter videos with synchronized audio, optional image and driving audio, and durations up to 20 seconds.',
        features: ['Start frame', 'Audio', 'Driving audio'],
        website: 'https://fal.ai/models/creatify/boreal',
        priceUsd: modelPriceUsd('boreal'),
      },
    ],
  },
  {
    category: 'Happy Horse',
    models: [
      {
        key: 'happy-horse',
        name: 'Happy Horse 1.0',
        slug: 'happy-horse',
        description:
          "Alibaba's #1-ranked video model with native audio, reference-to-video support (up to 9 images), and durations up to 15 seconds.",
        features: ['Start frame', 'Audio', 'Reference images'],
        website: 'https://fal.ai/models/alibaba/happy-horse/image-to-video',
        priceUsd: modelPriceUsd('happy-horse'),
      },
    ],
  },
  {
    category: 'Wan',
    models: [
      {
        key: 'wan-3.0',
        name: 'Wan 3.0',
        slug: 'wan-3-0',
        description:
          "Alibaba's Wan 3.0 — native 30-second takes at up to 1080p, start and end frames, toggleable native audio, and image, video, or audio references.",
        features: ['Start frame', 'End frame', 'Audio', 'Reference media', 'Auto duration', '30s'],
        website: 'https://fal.ai/models/alibaba/wan-3.0/text-to-video',
        priceUsd: modelPriceUsd('wan-3.0'),
      },
      {
        key: 'wan-3.0-prime',
        name: 'Wan 3.0 Prime',
        slug: 'wan-3-0-prime',
        description:
          "Alibaba's accelerated Wan 3.0 tier — faster generation with the same 30-second takes, 1080p output, native audio, and multimodal references.",
        features: ['Faster generation', 'Start frame', 'End frame', 'Audio', 'Reference media', 'Auto duration'],
        website: 'https://fal.ai/models/alibaba/wan-3.0-prime/text-to-video',
        priceUsd: modelPriceUsd('wan-3.0-prime'),
      },
    ],
  },
  {
    category: 'Luma',
    models: [
      {
        key: 'luma-ray-3.2-v2v',
        name: 'Luma Ray 3.2',
        slug: 'luma-ray-3-2',
        description:
          "Luma's video-to-video model — re-render an existing clip into new cinematic motion from a text prompt, with an optional start frame, at up to 1080p.",
        features: ['Video to video', 'Start frame'],
        website: 'https://fal.ai/models/luma/agent/ray/v3.2/video-to-video',
        priceUsd: modelPriceUsd('luma-ray-3.2-v2v'),
      },
    ],
  },
  {
    category: 'Gemini Omni',
    models: [
      {
        key: 'gemini-omni-flash',
        name: 'Gemini Omni 1.1 Flash',
        slug: 'gemini-omni-flash',
        description:
          "Google's Gemini Omni 1.1 Flash — fast text-to-video, image-to-video, and reference-to-video with native synchronized audio and role-bound reference images and clips. Draft at 360p, finish at 4K.",
        features: ['Start + end frame', 'Reference images', 'Reference videos', 'Elements', 'Audio', 'Up to 4K'],
        website: 'https://deepmind.google/models/gemini/',
        priceUsd: modelPriceUsd('gemini-omni-flash'),
      },
    ],
  },
]

export const STILL_EDITING_MODELS: AIModel[] = [
  {
    key: 'qwen-image-3',
    name: 'Qwen Image 3.0 Pro',
    slug: 'qwen-image-3',
    description:
      'High-fidelity image generation for dense typography, layouts, and precise edits. Add up to three references to preserve identity and details.',
    features: ['Reference images', 'Dense typography', 'Precise editing'],
    website: 'https://fal.ai/models/fal-ai/qwen-image-3/pro/text-to-image',
    priceUsd: modelPriceUsd('qwen-image-3'),
  },
  {
    key: 'grok-imagine-image-2',
    name: 'Grok Imagine Image 2.0',
    slug: 'grok-imagine-image-2',
    description:
      "xAI's image model for sharp typography and complex layouts, with photorealistic fidelity across photography, design, and illustration. Add up to three references to edit elements and styles.",
    features: ['Reference images', 'Sharp typography', 'Photorealistic fidelity'],
    website: 'https://fal.ai/models/xai/grok-imagine-image/v2.0/text-to-image',
    priceUsd: modelPriceUsd('grok-imagine-image-2'),
  },
  {
    key: 'flux-3-image',
    name: 'FLUX.3 Image',
    slug: 'flux-3-image',
    description:
      "Black Forest Labs' image model for native 2K and 4K output with precise layout and text. Add up to ten references to make targeted edits that keep everything else intact.",
    features: ['Up to 10 reference images', 'Native 4K', 'Targeted edits'],
    website: 'https://fal.ai/models/blackforestlabs/flux-3/text-to-image',
    priceUsd: modelPriceUsd('flux-3-image'),
  },
  {
    key: 'reve',
    name: 'Reve 2.1',
    slug: 'reve-2-1',
    description:
      'Native 4K image generation with precise layout planning, spatial control, and multilingual text. Add references to edit or remix.',
    features: ['Reference images', 'Native 4K', 'Strong text rendering'],
    website: 'https://reve.com/',
    priceUsd: modelPriceUsd('reve'),
  },
  {
    key: 'nano-banana-2',
    name: 'Nano Banana 2',
    slug: 'nano-banana-2',
    description:
      'Fast, affordable image generation with excellent typography and realism. Supports multi-image blending with character consistency.',
    features: ['Reference images', 'Web search'],
    website: 'https://gemini.google/overview/image-generation/',
    priceUsd: modelPriceUsd('nano-banana-2'),
  },
  {
    key: 'nano-banana-2.1',
    name: 'Nano Banana 2.1',
    slug: 'nano-banana-2-1',
    description:
      "Google's update to Nano Banana 2 with sharper realism, cleaner text and infographics, and steadier characters across edits. Blend up to 14 reference images.",
    features: ['Up to 14 reference images', 'Web search', 'Up to 4K'],
    website: 'https://fal.ai/models/fal-ai/nano-banana-2.1',
    priceUsd: modelPriceUsd('nano-banana-2.1'),
  },
  {
    key: 'nano-banana-2-lite',
    name: 'Nano Banana 2 Lite',
    slug: 'nano-banana-2-lite',
    description:
      "Google's fastest, most affordable image model. Fixed 1K output with reference-image blending and natural-language editing.",
    features: ['Reference images'],
    website: 'https://gemini.google/overview/image-generation/',
    priceUsd: modelPriceUsd('nano-banana-2-lite'),
  },
  {
    key: 'seedream-v5-lite',
    name: 'Seedream 5 Lite',
    slug: 'seedream-v5-lite',
    description:
      "ByteDance's fast, affordable image model. High-resolution 2K–4K output with flexible aspect ratios. Add reference images to edit or blend.",
    features: ['Reference images', '2K–4K output'],
    website: 'https://www.bytedance.com/',
    priceUsd: modelPriceUsd('seedream-v5-lite'),
  },
  {
    key: 'seedream-v5-pro',
    name: 'Seedream 5 Pro',
    slug: 'seedream-v5-pro',
    description:
      "ByteDance's flagship image model. Sharp high-resolution output with strong text rendering and flexible aspect ratios. Add reference images to edit or blend.",
    features: ['Reference images', 'Strong text rendering'],
    website: 'https://www.bytedance.com/',
    priceUsd: modelPriceUsd('seedream-v5-pro'),
  },
  {
    key: 'nano-banana-pro-2',
    name: 'Nano Banana Pro',
    slug: 'nano-banana-pro',
    description:
      'State-of-the-art image generation with excellent typography and realism. Supports multi-image blending with character consistency.',
    features: ['Reference images', 'Web search'],
    website: 'https://gemini.google/overview/image-generation/',
    priceUsd: modelPriceUsd('nano-banana-pro-2'),
  },
  {
    key: 'flux-2-max',
    name: 'Flux 2 Max',
    slug: 'flux-2-max',
    description:
      'High-quality image generation with flexible aspect ratios and safety controls. Add reference images for creative editing and blending.',
    features: ['Reference images'],
    website: 'https://bfl.ai/',
    priceUsd: modelPriceUsd('flux-2-max'),
  },
  {
    key: 'gpt-image-2',
    name: 'GPT Image 2',
    slug: 'gpt-image-2',
    description:
      "OpenAI's GPT Image 2 — photoreal image generation with strong typography and instruction following. Add reference images to edit an existing image.",
    features: ['Reference images'],
    website: 'https://openai.com/',
    priceUsd: modelPriceUsd('gpt-image-2'),
  },
  {
    key: 'gpt-image-2.5-flare',
    name: 'GPT Image 2.5 Flare',
    slug: 'gpt-image-2-5-flare',
    description:
      "OpenAI's GPT Image 2.5 Flare — fast, high-quality image generation with natural lighting, rich textures, and precise instruction following. Add reference images to edit an existing image; edits change only what you ask for.",
    features: ['Reference images'],
    website: 'https://openai.com/',
    priceUsd: modelPriceUsd('gpt-image-2.5-flare'),
  },
  {
    key: 'gpt-image-2.5-sunburst',
    name: 'GPT Image 2.5 Sunburst',
    slug: 'gpt-image-2-5-sunburst',
    description:
      "OpenAI's GPT Image 2.5 Sunburst — precision image generation for polished product imagery and campaign creative, with tighter control across edits. Slower than Flare. Add reference images to edit an existing image.",
    features: ['Reference images'],
    website: 'https://openai.com/',
    priceUsd: modelPriceUsd('gpt-image-2.5-sunburst'),
  },
  {
    key: 'ideogram-v4.5',
    name: 'Ideogram 4.5',
    slug: 'ideogram-4-5',
    description:
      "Ideogram's precise image model. Typography renders exactly as written in any language, and edits change only what you ask for, so a run of edits stays clean. Edit with a painted region or guide the change with up to four reference images.",
    features: ['Reference images', 'Exact typography', 'Precise editing', 'Region edit'],
    website: 'https://fal.ai/models/ideogram/v4.5',
    priceUsd: modelPriceUsd('ideogram-v4.5'),
  },
]

export const AUDIO_GENERATION_MODELS: AIModel[] = [
  {
    key: 'minimax-music',
    name: 'MiniMax Music',
    slug: 'minimax-music',
    description: 'Generate complete music tracks from a prompt, with optional vocals and custom lyrics.',
    features: ['Music', 'Instrumental or vocals', 'Custom lyrics'],
    website: 'https://www.minimax.io/audio/music',
    priceUsd: modelPriceUsd('minimax-music'),
  },
  {
    key: 'minimax-music-3',
    name: 'MiniMax Music 3',
    slug: 'minimax-music-3',
    description:
      'Generate a complete song up to five minutes long, holding theme, vocal identity and arrangement steady across the whole track.',
    features: ['Music', 'Up to 5 minutes', 'Custom lyrics'],
    website: 'https://www.minimax.io/audio/music',
    priceUsd: modelPriceUsd('minimax-music-3'),
  },
  {
    key: 'lyria-3.5',
    name: 'Lyria 3.5',
    slug: 'lyria-3-5',
    description:
      'Generate complete songs up to about three minutes with richer musicality, lyrics, and multilingual vocals.',
    features: ['Music', 'Up to 3 minutes', 'Multilingual vocals'],
    website: 'https://deepmind.google/models/lyria/',
    priceUsd: modelPriceUsd('lyria-3.5'),
  },
  {
    key: 'lyria3',
    name: 'Lyria 3',
    slug: 'lyria-3',
    description: 'Generate 30-second music sketches with singing vocals and negative-prompt control.',
    features: ['Music', '30 seconds', 'Vocals'],
    website: 'https://deepmind.google/models/lyria/',
    priceUsd: modelPriceUsd('lyria3'),
  },
  {
    key: 'lyria-3-pro',
    name: 'Lyria 3 Pro',
    slug: 'lyria-3-pro',
    description: 'Generate complete songs up to about three minutes with vocals, timed lyrics, and full arrangements.',
    features: ['Music', 'Up to 3 minutes', 'Timed lyrics'],
    website: 'https://blog.google/innovation-and-ai/technology/ai/lyria-3-pro/',
    priceUsd: modelPriceUsd('lyria-3-pro'),
  },
  {
    key: 'elevenlabs-music',
    name: 'ElevenLabs Music v2.5',
    slug: 'elevenlabs-music',
    description:
      'Generate vocal or instrumental music from three seconds to ten minutes with Eleven Music v2.5, with optional section planning.',
    features: ['Music v2.5', '3 seconds–10 minutes', 'Section planning'],
    website: 'https://elevenlabs.io/music',
    priceUsd: modelPriceUsd('elevenlabs-music'),
  },
  {
    key: 'stable-audio-25',
    name: 'Stable Audio 2.5',
    slug: 'stable-audio-2-5',
    description: 'Generate stereo music, foley, and ambient sound design up to 190 seconds.',
    features: ['Music and SFX', 'Up to 190 seconds', 'Stereo'],
    website: 'https://stableaudio.com/',
    priceUsd: modelPriceUsd('stable-audio-25'),
  },
  {
    key: 'seed-audio-1.0',
    name: 'Seed Audio 1.0',
    slug: 'seed-audio-1-0',
    description: 'Generate dialogue, sound effects, and music together from one prompt, or create clean speech.',
    features: ['Music, SFX, and dialogue', 'Up to 2 minutes', 'Voice presets'],
    website: 'https://seed.bytedance.com/en/models',
    priceUsd: modelPriceUsd('seed-audio-1.0'),
  },
  {
    key: 'elevenlabs-sfx-v2',
    name: 'ElevenLabs SFX v2',
    slug: 'elevenlabs-sfx-v2',
    description: 'Generate short foley, ambience, impacts, and atmospheric sounds from text.',
    features: ['Sound effects', '0.5–22 seconds', 'Text to SFX'],
    website: 'https://elevenlabs.io/sound-effects',
    priceUsd: modelPriceUsd('elevenlabs-sfx-v2'),
  },
  {
    key: 'beatoven-sfx',
    name: 'Beatoven SFX',
    slug: 'beatoven-sfx',
    description: 'Generate stereo effects for creatures, vehicles, impacts, science fiction, and abstract textures.',
    features: ['Sound effects', '1–35 seconds', 'Stereo'],
    website: 'https://www.beatoven.ai/maestro/sound-effects',
    priceUsd: modelPriceUsd('beatoven-sfx'),
  },
  {
    key: 'cassetteai-sfx',
    name: 'CassetteAI SFX',
    slug: 'cassetteai-sfx',
    description: 'Generate fast sound-effect placeholders and draft variations up to 30 seconds.',
    features: ['Sound effects', '1–30 seconds', 'Fast generation'],
    website: 'https://cassetteai.com/',
    priceUsd: modelPriceUsd('cassetteai-sfx'),
  },
  {
    key: 'sonilo-music',
    name: 'Sonilo Music',
    slug: 'sonilo-music',
    description:
      'Generate licensed, commercial-use-safe music from text, or add a source video for a soundtrack synced to its pacing and mood.',
    features: ['Text to music', 'Video-synced soundtrack', '10–600 seconds', 'Licensed for commercial use'],
    website: 'https://fal.ai/models/sonilo/v1.1/text-to-music',
    priceUsd: modelPriceUsd('sonilo-music'),
  },
  {
    key: 'sonilo-sfx',
    name: 'Sonilo SFX',
    slug: 'sonilo-sfx',
    description:
      'Generate standalone sound effects from text, or add a source video for frame-synchronized foley and ambience.',
    features: ['Text to sound effects', 'Video synchronization', '1–180 seconds', 'AAC, MP3, WAV, FLAC'],
    website: 'https://fal.ai/models/sonilo/v1.1/text-to-sound-effects',
    priceUsd: modelPriceUsd('sonilo-sfx'),
  },
  {
    key: 'deepfilternet3',
    name: 'DeepFilterNet 3',
    slug: 'deepfilternet3',
    description:
      'Reduce background noise on dialogue and upsample it to 48 kHz. Works on an audio clip or straight from a video node, and the clean track can replace the audio on the source video.',
    features: ['Noise reduction', '48 kHz upsampling', 'Audio or video source', 'Replace audio on source video'],
    website: 'https://fal.ai/models/fal-ai/deepfilternet3',
    priceUsd: modelPriceUsd('deepfilternet3'),
  },
  {
    key: 'veed-clean-audio',
    name: 'VEED Clean Audio',
    slug: 'veed-clean-audio',
    description:
      'Remove background noise from dialogue while keeping quiet and distant words, then level it to broadcast loudness. Works on an audio clip or straight from a video node, up to 30 minutes.',
    features: ['Noise removal', 'Keeps quiet words', '-19 LUFS loudness', 'Audio or video source, up to 30 min'],
    website: 'https://fal.ai/models/veed/clean-audio',
    priceUsd: modelPriceUsd('veed-clean-audio'),
  },
  {
    key: 'elevenlabs-tts-v3',
    name: 'ElevenLabs TTS v3',
    slug: 'elevenlabs-tts-v3',
    description: 'Generate expressive dialogue and voiceover with preset voices, language control, and delivery tags.',
    features: ['Dialogue and voiceover', '21 voices', '70+ languages'],
    website: 'https://elevenlabs.io/v3',
    priceUsd: modelPriceUsd('elevenlabs-tts-v3'),
  },
  {
    key: 'elevenlabs-tts-v4',
    name: 'ElevenLabs TTS v4',
    slug: 'elevenlabs-tts-v4',
    description:
      'Generate expressive dialogue and voiceover, directing delivery with inline audio tags and shifting emotion mid-sentence.',
    features: ['Dialogue and voiceover', '21 voices', '80+ languages', 'Audio tags'],
    website: 'https://fal.ai/models/elevenlabs/tts/eleven-v4',
    priceUsd: modelPriceUsd('elevenlabs-tts-v4'),
  },
  {
    key: 'elevenlabs-tts-v4-turbo',
    name: 'ElevenLabs TTS v4 Turbo',
    slug: 'elevenlabs-tts-v4-turbo',
    description: 'Generate voiceover fast and at half the price of v4, with the same voices and audio tags.',
    features: ['Dialogue and voiceover', '21 voices', '80+ languages', 'Low latency'],
    website: 'https://fal.ai/models/elevenlabs/tts/eleven-v4-turbo',
    priceUsd: modelPriceUsd('elevenlabs-tts-v4-turbo'),
  },
  {
    key: 'minimax-speech-02-hd',
    name: 'MiniMax Speech-02 HD',
    slug: 'minimax-speech-02-hd',
    description: 'Generate natural narration and character dialogue with broad voice and language support.',
    features: ['Dialogue and voiceover', '17 voices', '40+ languages'],
    website: 'https://www.minimax.io/audio/text-to-speech',
    priceUsd: modelPriceUsd('minimax-speech-02-hd'),
  },
  {
    key: 'gemini-3.8-flash-tts',
    name: 'Gemini 3.8 Flash TTS',
    slug: 'gemini-3-8-flash-tts',
    description:
      'Generate expressive dialogue and voiceover, directing delivery in plain language: character, accent, pacing, and emotion.',
    features: ['Dialogue and voiceover', '30 voices', 'Style direction'],
    website: 'https://ai.google.dev/gemini-api/docs/speech-generation',
    priceUsd: modelPriceUsd('gemini-3.8-flash-tts'),
  },
  {
    key: 'gemini-3.8-flash-lite-tts',
    name: 'Gemini 3.8 Flash Lite TTS',
    slug: 'gemini-3-8-flash-lite-tts',
    description:
      'Generate high-volume voiceover fast and at low cost, with the same voices and style direction as Flash TTS.',
    features: ['Dialogue and voiceover', '30 voices', 'Style direction'],
    website: 'https://ai.google.dev/gemini-api/docs/speech-generation',
    priceUsd: modelPriceUsd('gemini-3.8-flash-lite-tts'),
  },
]

export const SETS_MODELS: AIModel[] = [
  {
    key: 'worldlabs-marble-v1',
    name: 'Set',
    slug: 'set',
    description: 'Generate a 3D set from an image using WorldLabs Marble.',
    features: [],
    website: '',
    priceUsd: modelPriceUsd('worldlabs-marble-v1'),
  },
  {
    key: 'set-screenshot',
    name: 'Set Photo',
    slug: 'set-photo',
    description: 'Capture a screenshot from within the 3D set viewer.',
    features: [],
    website: '',
    priceUsd: 0,
  },
]

// Flatten video models for easy iteration
export const ALL_VIDEO_MODELS: AIModel[] = VIDEO_GENERATION_MODELS.flatMap(category => category.models)

/**
 * The roster a marketing page publishes right now: every catalog with models
 * past their sunset removed, decided on one clock so no list disagrees with
 * another. Call it once per render and hand the pieces down — the pricing
 * calculator takes them as props — so server markup and client hydrate from
 * the same decision. The pages that use it are ISR, which is what lets a
 * retired model leave them on the day without a deploy.
 */
export function offeredMarketingCatalog(now: number = Date.now()): {
  videoGroups: ModelCategory[]
  videoModels: AIModel[]
  stillModels: AIModel[]
  audioModels: AIModel[]
} {
  return {
    videoGroups: omitSunsetCategories(VIDEO_GENERATION_MODELS, now),
    videoModels: omitSunsetModels(ALL_VIDEO_MODELS, now),
    stillModels: omitSunsetModels(STILL_EDITING_MODELS, now),
    audioModels: omitSunsetModels(AUDIO_GENERATION_MODELS, now),
  }
}

// ============================================
// Model Logos
// ============================================

export interface ModelLogo {
  src: string
  alt: string
  name: string
}

export const MODEL_LOGOS: ModelLogo[] = [
  { src: '/marketing/klingai.png', alt: 'Kling AI Logo', name: 'Kling AI' },
  { src: '/marketing/veo3-text.jpg', alt: 'Google Veo 3 Logo', name: 'Veo 3' },
  { src: '/marketing/logos/Screenshot_2025-08-20.png', alt: 'Minimax Logo', name: 'Minimax' },
  { src: '/marketing/logos/logo_white_on_black.png', alt: 'Seedance Logo', name: 'Seedance' },
  { src: '/marketing/logos/soralogo.png', alt: 'OpenAI Sora Logo', name: 'Sora' },
]

// ============================================
// Film Gallery Data
// ============================================

export interface Film {
  id: string
  title: string
  creator: string
  creatorUrl: string
  filmUrl: string
  thumbnail: string
}

export const FEATURED_FILMS: Film[] = [
  {
    id: 'geely-ex5-stefan',
    title: 'Geely EX5',
    creator: 'Stefan Aberer',
    creatorUrl: 'https://stefan-aberer.at/',
    filmUrl: 'https://vimeo.com/1135132373/1242479337',
    thumbnail: '/marketing/thumbs/new/geely-ex5.jpg',
  },
  {
    id: 'between-the-lines-oscar',
    title: 'Between the Lines',
    creator: 'Oscar Burgos',
    creatorUrl: 'https://www.imdb.com/name/nm4443508/',
    filmUrl: 'https://www.youtube.com/watch?v=J5DBrX75cgM',
    thumbnail: '/marketing/thumbs/new/between-the-lines.jpg',
  },
  {
    id: 'ride-koh',
    title: 'RIDE',
    creator: 'Koh Terai',
    creatorUrl: 'https://www.kohterai.com/',
    filmUrl: 'https://www.youtube.com/watch?v=BhKNQejQDTA',
    thumbnail: '/marketing/thumbs/new/ride-commercial.jpg',
  },
  {
    id: 'emptyvessels-gami',
    title: 'Empty Vessels',
    creator: 'Gami Orbegoso',
    creatorUrl: 'https://www.instagram.com/gamiorbegoso/',
    filmUrl: 'https://www.instagram.com/reel/DTGUropDXsi/',
    thumbnail: '/marketing/thumbs/new/emptyvessels.jpg',
  },
  {
    id: 'mechasailors-choybokk',
    title: 'Mecha Sailors',
    creator: 'Choybokk',
    creatorUrl: 'https://www.youtube.com/@choybokk',
    filmUrl: 'https://www.youtube.com/watch?v=9AQ9HuTcZZg',
    thumbnail: '/marketing/thumbs/new/MechaSailors.jpg',
  },
  {
    id: 'someonemisses-elad',
    title: 'Someone Misses Your Call',
    creator: 'Elad Segoli',
    creatorUrl: 'https://www.eladsegoli.com/',
    filmUrl: 'https://vimeo.com/1139944493',
    thumbnail: '/marketing/thumbs/new/SomeoneMissesYourCall.jpg',
  },
]

// ============================================
// Team & Credits
// ============================================

export interface TeamMember {
  role: string
  name: string
  url: string
}

export interface CreativePartner {
  name: string
  url: string
}

export const TEAM_MEMBERS: TeamMember[] = [
  {
    role: 'DIRECTOR OF PHOTOGRAPHY',
    name: 'Koh Terai',
    url: 'https://www.kohterai.com/',
  },
  {
    role: 'DIRECTOR OF TECHNOLOGY',
    name: 'Long Hoang',
    url: 'https://www.linkedin.com/in/longh/',
  },
  {
    role: 'PRODUCT',
    name: 'Jane Zhang',
    url: 'https://imjane.top/',
  },
]

export const CREATIVE_PARTNERS: CreativePartner[] = [
  { name: 'Gami Orbegoso', url: 'https://www.instagram.com/gamiorbegoso/' },
  { name: 'Choybokk', url: 'https://www.youtube.com/@choybokk' },
  { name: 'Andy Perez', url: 'https://www.youtube.com/@Cinex_Ai1' },
  { name: 'Stefan Aberer', url: 'https://stefan-aberer.at/' },
  { name: 'Elad Segoli', url: 'https://www.eladsegoli.com/' },
]

export interface Engineer {
  name: string
  url?: string
}

export const ENGINEERS: Engineer[] = [
  { name: 'Ravern Koh', url: 'https://www.linkedin.com/in/ravern/' },
  { name: 'Matthew King', url: 'https://matthewking.site' },
]

// ============================================
// FAQ Data
// ============================================

export interface FAQ {
  id: string
  question: string
  answer: string
}

export const FAQS: FAQ[] = [
  {
    id: 'faq-veo-3-premiere',
    question: 'Can I use Veo 3 with my current editing software?',
    answer:
      'Yes. Martini is a web-based editor that lets you generate Veo 3 shots directly in the browser. You can then export your project as XML and import it seamlessly into Adobe Premiere Pro, DaVinci Resolve, or any other editing software.',
  },
  {
    id: 'faq-kling-start-end',
    question: 'Does Martini support start and end frames with Kling?',
    answer:
      'Yes. Kling 4, Kling 3.0 Pro, Kling O3 Pro, and Kling O1 all support start and end frame control, enabling tight in/out precision for editorial workflows. This allows you to match your AI-generated content exactly to your existing shots for seamless continuity.',
  },
  {
    id: 'faq-supported-models',
    question: 'What AI video models does Martini support?',
    answer:
      'Martini supports leading AI video generation models including Veo 3.1, Kling 4, Kling 3.0 Pro, Kling O3 Pro, Kling 2.6, Kling O1, Sora 2 Pro, MiniMax H3, Seedance 2.5, Wan 3.0 Prime, Wan 3.0, Hailuo 02, Seedance 1.5, and Grok Imagine. For still image generation and editing, we support Nano Banana Pro and Flux 2 Max. All models are available directly in the Martini web editor, allowing you to choose the best model for each shot in a single unified interface.',
  },
  {
    id: 'faq-still-image',
    question: 'Does Martini support still image generation?',
    answer:
      'Yes. Martini supports Nano Banana Pro for high-quality image generation with excellent typography and multi-image blending, and Flux 2 Max for flexible aspect ratios and creative editing with reference images. Generate reference frames directly in your timeline.',
  },
  {
    id: 'faq-audio-generation',
    question: 'What audio generation models does Martini support?',
    answer:
      'For music, Martini supports Sonilo Music, MiniMax Music, Lyria 3.5, Lyria 3, Lyria 3 Pro, ElevenLabs Music v2.5, and Stable Audio 2.5. Sound-effect models include ElevenLabs SFX v2, Beatoven SFX, CassetteAI SFX, Sonilo SFX, and Stable Audio 2.5. Dialogue and voiceover models include ElevenLabs TTS v3, MiniMax Speech-02 HD, Gemini 3.8 Flash TTS, and Gemini 3.8 Flash Lite TTS. Seed Audio 1.0 can generate music, sound effects, and dialogue together.',
  },
  {
    id: 'faq-editors',
    question: 'What video editors work with Martini?',
    answer:
      'Martini works with any professional video editor that supports XML import, including Adobe Premiere Pro and DaVinci Resolve. Simply create in our web editor and export your timeline to XML. This gives you the flexibility of a cloud-based AI studio with the power of your preferred desktop NLE.',
  },
  {
    id: 'faq-end-frame',
    question: 'Does Martini support end frame control?',
    answer:
      'Yes. End frame control is available with Veo 3.1, Kling 4, Kling 3.0 Pro, Kling O3 Pro, Kling O1, Hailuo 02, Seedance 1.5, Seedance 2.5, MiniMax H3, Wan 3.0 Prime, and Wan 3.0, giving you precise control over both the start and end frames of your AI-generated videos for tighter editorial workflows. This feature is essential for matching existing shots and maintaining continuity in professional productions.',
  },
  {
    id: 'faq-differentiators',
    question: 'What makes Martini different from other AI video tools?',
    answer:
      'Martini combines a professional timeline interface with AI generation in the browser. Instead of managing scattered video files, you build your sequence in our web editor and export to XML, seamlessly bridging the gap between AI generation and professional post-production.',
  },
  {
    id: 'faq-partnership',
    question: 'Do you have a creative partnership program?',
    answer:
      'Yes. We offer a creative partnership program for studios, agencies, and independent creators working on notable projects. Partners receive priority support, early access to new features, collaborative opportunities, and custom integrations. Contact us at koh@c47.studio to learn more about partnership opportunities.',
  },
]

// ============================================
// Background Videos
// ============================================

export interface VideoConfig {
  src: string
  poster?: string
  creditLabel: string
  creditTitle: string
  creditUrl: string
}

export const BACKGROUND_VIDEOS: VideoConfig[] = [
  {
    src: 'https://cdn.martini.film/public/Navidad.mp4',
    poster: '/marketing/thumbs/navidad.jpg',
    creditLabel: "Gami Orbegoso's film",
    creditTitle: '"Navidad"',
    creditUrl: 'https://www.instagram.com/p/DSnXQMNDX_E/',
  },
  {
    src: 'https://cdn.martini.film/public/goldengate.mp4',
    poster: '/marketing/thumbs/goldengate.jpg',
    creditLabel: "Gami Orbegoso's film",
    creditTitle: '"Golden Gate"',
    creditUrl: 'https://www.youtube.com/watch?v=BA5IAG9hov8',
  },
]
